// Zone/Bölge System - Turkish Mythology ARPG
// Defines different areas with unique characteristics
import { t } from '@locales/index';

export type ZoneType =
  // Act 1
  | 'otag_koyu'         // Hub - Safe zone (Otağ Köyü)
  | 'karanlik_orman'    // Dark Forest - Act 1 starting area
  | 'ruh_batakligi'     // Spirit Swamp - Darker, more spirits
  | 'erlik_tapinagi'    // Erlik Temple - Act 1 final area
  // Act 2 - Ergenekon
  | 'ergenekon_girisi'  // Ergenekon Entrance (Iron Mountains Pass)
  | 'firtina_ovasi'     // Storm Steppe
  | 'ates_magarasi'     // Fire Cave
  | 'yersub_tahti'      // Yer-Sub Throne (Boss)
  // Act 3 - Yeraltı
  | 'karanlik_tunel'    // Dark Tunnel
  | 'olum_nehri'        // River of Death
  | 'kemik_ormani'      // Bone Forest
  | 'lanetli_saray'     // Cursed Palace
  // Act 4 - Erlik Diyarı
  | 'erlik_kapisi'      // Erlik's Gate
  | 'cehennem_bahcesi'  // Hell Garden
  | 'taht_odasi';       // Throne Room (Final Boss)

export type EnemySpawnType =
  // Act 1 enemies
  | 'kormos'
  | 'al_kormos'
  | 'karabasan'
  | 'albasti'
  | 'karakura'
  | 'sulbus'
  | 'erlik_okcusu'
  | 'erlik_guard'
  | 'albasti_kralicesi'
  // Act 2 enemies
  | 'demir_kurt'
  | 'dag_devi'
  | 'ruzgar_ruhu'
  | 'ates_iblisi'
  | 'kaya_golemi'
  | 'yer_sub'
  // Act 3 enemies
  | 'golge_avcisi'
  | 'kemik_savasci'
  | 'lanetli_ruh'
  | 'olum_habercisi'
  | 'erlik_elcisi'
  // Act 4 enemies
  | 'erlik_sovalyesi'
  | 'karanlik_seraf'
  | 'erlik_han';

export interface EnemySpawnConfig {
  type: EnemySpawnType;
  weight: number;      // Spawn chance weight
  minLevel?: number;   // Min player level to spawn
  maxCount?: number;   // Max on screen at once
  packSize?: number;   // For pack spawning
}

export interface ZoneConfig {
  id: ZoneType;
  name: string;
  description: string;

  // Visual
  tilesetId: string;
  ambientColor: string;      // Tint for ambient lighting
  fogColor?: string;         // Optional fog overlay
  fogDensity?: number;       // 0-1

  // Gameplay
  levelRange: { min: number; max: number };
  isSafeZone: boolean;       // No enemies in safe zones

  // Enemy spawning
  enemySpawns: EnemySpawnConfig[];
  maxEnemies: number;
  spawnInterval: number;     // Seconds between spawns

  // Boss
  bossType?: EnemySpawnType;

  // Music/Audio theme
  musicTheme?: string;
  ambientSounds?: string[];

  // Connections to other zones
  exits: ZoneExit[];

  // Waypoint position (for towns/zones with waypoints)
  waypointPosition?: { x: number; y: number };
}

export interface ZoneExit {
  targetZone: ZoneType;
  position: { x: number; y: number };
  portalType: 'path' | 'portal' | 'gate';
  requiresLevel?: number;
  requiresItem?: string;
}

// Zone definitions
export const ZONES: Record<ZoneType, ZoneConfig> = {
  otag_koyu: {
    id: 'otag_koyu',
    name: 'Otağ Köyü',
    description: 'Güvenli bir şaman köyü. Burada dinlenebilir ve hazırlanabilirsin.',
    tilesetId: 'village',
    ambientColor: '#f0e8c8',
    fogColor: '#e8dfc0',
    fogDensity: 0.08,
    levelRange: { min: 1, max: 99 },
    isSafeZone: true,
    enemySpawns: [],
    maxEnemies: 0,
    spawnInterval: 0,
    musicTheme: 'village_peaceful',
    waypointPosition: { x: 160, y: 1520 },
    exits: [
      {
        targetZone: 'karanlik_orman',
        position: { x: 1504, y: 848 },
        portalType: 'path'
      }
    ]
  },

  karanlik_orman: {
    id: 'karanlik_orman',
    name: 'Karanlık Orman',
    description: 'Körmösler ve kötü ruhların gezindiği tehlikeli bir orman.',
    tilesetId: 'dark_forest',
    ambientColor: '#1a2f1a',
    fogColor: '#0d1f0d',
    fogDensity: 0.3,
    levelRange: { min: 1, max: 5 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'kormos', weight: 40 },
      { type: 'al_kormos', weight: 10, minLevel: 2 },
      { type: 'karakura', weight: 20, packSize: 3 },
      { type: 'sulbus', weight: 15, minLevel: 3 },
      { type: 'karabasan', weight: 15, minLevel: 2 }
    ],
    maxEnemies: 12,
    spawnInterval: 4,
    musicTheme: 'dark_forest',
    ambientSounds: ['wind', 'wolves', 'crows'],
    waypointPosition: { x: -1920, y: 1760 },
    exits: [
      {
        targetZone: 'otag_koyu',
        position: { x: -2560, y: 1952 },
        portalType: 'path'
      },
      {
        targetZone: 'ruh_batakligi',
        position: { x: 3072, y: 1664 },
        portalType: 'path',
        requiresLevel: 3
      }
    ]
  },

  ruh_batakligi: {
    id: 'ruh_batakligi',
    name: 'Ruh Bataklığı',
    description: 'Kayıp ruhların sığınağı. Albastıların yuvası.',
    tilesetId: 'swamp',
    ambientColor: '#2d1f3d',
    fogColor: '#1a0f2a',
    fogDensity: 0.5,
    levelRange: { min: 3, max: 7 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'kormos', weight: 20 },
      { type: 'al_kormos', weight: 15 },
      { type: 'albasti', weight: 30 },
      { type: 'karabasan', weight: 20 },
      { type: 'sulbus', weight: 15 }
    ],
    maxEnemies: 12,             // Reduced from 15 for mid-level zone
    spawnInterval: 4,           // Slightly slower spawns
    musicTheme: 'swamp_eerie',
    ambientSounds: ['bubbles', 'whispers', 'splashing'],
    waypointPosition: { x: -640, y: 2240 },
    exits: [
      {
        targetZone: 'karanlik_orman',
        position: { x: -928, y: 2672 },
        portalType: 'path'
      },
      {
        targetZone: 'erlik_tapinagi',
        position: { x: 3008, y: 1664 },
        portalType: 'gate',
        requiresLevel: 5
      }
    ]
  },

  erlik_tapinagi: {
    id: 'erlik_tapinagi',
    name: 'Erlik Tapınağı',
    description: 'Yeraltı tanrısı Erlik\'e adanmış antik tapınak. Act 1 final bölgesi.',
    tilesetId: 'temple',
    ambientColor: '#1a0a0a',
    fogColor: '#0d0505',
    fogDensity: 0.4,
    levelRange: { min: 5, max: 10 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'al_kormos', weight: 20 },
      { type: 'erlik_okcusu', weight: 25 },
      { type: 'erlik_guard', weight: 15 },
      { type: 'sulbus', weight: 20 },
      { type: 'albasti', weight: 20 }
    ],
    maxEnemies: 15,            // Slightly fewer but tougher enemies
    spawnInterval: 3.5,
    bossType: 'albasti_kralicesi', // Act 1 final boss
    musicTheme: 'temple_dark',
    ambientSounds: ['drums', 'chants', 'fire_crackling'],
    waypointPosition: { x: -1472, y: 2304 },
    exits: [
      {
        targetZone: 'ruh_batakligi',
        position: { x: -2048, y: 2592 },
        portalType: 'gate'
      },
      {
        targetZone: 'ergenekon_girisi',
        position: { x: 1472, y: 832 },
        portalType: 'portal',
        requiresLevel: 10
      }
    ]
  },

  // ==========================================
  // ACT 2: ERGENEKON
  // ==========================================

  ergenekon_girisi: {
    id: 'ergenekon_girisi',
    name: 'Ergenekon Girişi',
    description: 'Demir Dağları Geçidi. Dar kayalık yollardan oluşan tehlikeli bir dağ geçidi.',
    tilesetId: 'mountain_entrance',
    ambientColor: '#5a6a5a',
    fogColor: '#3a4a3a',
    fogDensity: 0.2,
    levelRange: { min: 12, max: 18 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'demir_kurt', weight: 40, packSize: 3 },
      { type: 'kaya_golemi', weight: 20 },
      { type: 'ruzgar_ruhu', weight: 15 },
      { type: 'al_kormos', weight: 25 }
    ],
    maxEnemies: 18,
    spawnInterval: 3,
    musicTheme: 'ergenekon_epic',
    ambientSounds: ['wind_howl', 'wolves', 'rocks_falling'],
    waypointPosition: { x: 0, y: 1424 },
    exits: [
      {
        targetZone: 'erlik_tapinagi',
        // South portal: tile (50, 87) -> worldX=(50-87)*32=-1184, worldY=(50+87)*16=2192
        position: { x: -1184, y: 2192 },
        portalType: 'portal'
      },
      {
        targetZone: 'firtina_ovasi',
        // North portal: tile (50, 3) -> worldX=(50-3)*32=1504, worldY=(50+3)*16=848
        position: { x: 1504, y: 848 },
        portalType: 'path',
        requiresLevel: 15
      }
    ]
  },

  firtina_ovasi: {
    id: 'firtina_ovasi',
    name: 'Fırtına Ovası',
    description: 'Rüzgarların estiği geniş bozkır. Terk edilmiş göçebe kampları ve taş harabeler.',
    tilesetId: 'steppe',
    ambientColor: '#6a7a5a',
    fogColor: '#5a6a4a',
    fogDensity: 0.35,
    levelRange: { min: 16, max: 22 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'ruzgar_ruhu', weight: 35 },
      { type: 'ates_iblisi', weight: 20 },
      { type: 'demir_kurt', weight: 25, packSize: 2 },
      { type: 'kaya_golemi', weight: 20 }
    ],
    maxEnemies: 20,
    spawnInterval: 3,
    musicTheme: 'steppe_wind',
    ambientSounds: ['strong_wind', 'thunder', 'eagles'],
    waypointPosition: { x: -1504, y: 848 },
    exits: [
      {
        targetZone: 'ergenekon_girisi',
        // West portal: tile (3, 50) -> worldX=(3-50)*32=-1504, worldY=(3+50)*16=848
        position: { x: -1504, y: 848 },
        portalType: 'path'
      },
      {
        targetZone: 'ates_magarasi',
        // East portal: tile (117, 50) -> worldX=(117-50)*32=2144, worldY=(117+50)*16=2672
        position: { x: 2144, y: 2672 },
        portalType: 'path',
        requiresLevel: 19
      }
    ]
  },

  ates_magarasi: {
    id: 'ates_magarasi',
    name: 'Ateş Mağarası',
    description: 'Lav nehirlerinin aktığı volkanik mağara. Ateş iblislerinin yuvası.',
    tilesetId: 'fire_cave',
    ambientColor: '#6a3a2a',
    fogColor: '#4a2a1a',
    fogDensity: 0.35,
    levelRange: { min: 20, max: 28 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'ates_iblisi', weight: 40 },
      { type: 'dag_devi', weight: 15, maxCount: 2 },
      { type: 'kaya_golemi', weight: 25 },
      { type: 'demir_kurt', weight: 20 }
    ],
    maxEnemies: 20,
    spawnInterval: 2.8,
    musicTheme: 'volcanic_fury',
    ambientSounds: ['lava_bubbling', 'fire_roaring', 'explosions'],
    waypointPosition: { x: -1248, y: 720 },
    exits: [
      {
        targetZone: 'firtina_ovasi',
        // West portal: tile (3, 42) -> worldX=(3-42)*32=-1248, worldY=(3+42)*16=720
        position: { x: -1248, y: 720 },
        portalType: 'path'
      },
      {
        targetZone: 'yersub_tahti',
        // East portal: tile (87, 42) -> worldX=(87-42)*32=1440, worldY=(87+42)*16=2064
        position: { x: 1440, y: 2064 },
        portalType: 'gate',
        requiresLevel: 24
      }
    ]
  },

  yersub_tahti: {
    id: 'yersub_tahti',
    name: 'Yer-Sub Tahtı',
    description: 'Yer ve Su ruhlarının efendisi Yer-Sub\'un yeraltı sarayı. Act 2 final.',
    tilesetId: 'underground_palace',
    ambientColor: '#2a3a4a',
    fogColor: '#1a2a3a',
    fogDensity: 0.5,
    levelRange: { min: 25, max: 35 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'dag_devi', weight: 25, maxCount: 3 },
      { type: 'kaya_golemi', weight: 30 },
      { type: 'ates_iblisi', weight: 25 },
      { type: 'ruzgar_ruhu', weight: 20 }
    ],
    maxEnemies: 16,
    spawnInterval: 3,
    bossType: 'yer_sub',
    musicTheme: 'elemental_boss',
    ambientSounds: ['water_dripping', 'earth_rumbling', 'wind_echo'],
    waypointPosition: { x: -1824, y: 2192 },
    exits: [
      {
        targetZone: 'ates_magarasi',
        // South portal: tile (40, 97) -> worldX=(40-97)*32=-1824, worldY=(40+97)*16=2192
        position: { x: -1824, y: 2192 },
        portalType: 'gate'
      },
      {
        targetZone: 'karanlik_tunel',
        // North portal (throne area): tile (40, 3) -> worldX=(40-3)*32=1184, worldY=(40+3)*16=688
        position: { x: 1184, y: 688 },
        portalType: 'portal',
        requiresLevel: 30
      }
    ]
  },

  // ==========================================
  // ACT 3: YERALTI (Placeholder - will be expanded)
  // ==========================================

  karanlik_tunel: {
    id: 'karanlik_tunel',
    name: 'Karanlık Tünel',
    description: 'Yeraltına inen karanlık tünel. Act 3 başlangıcı.',
    tilesetId: 'dark_tunnel',
    ambientColor: '#1a1a2a',
    fogColor: '#0a0a1a',
    fogDensity: 0.6,
    levelRange: { min: 28, max: 32 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'golge_avcisi', weight: 35 },
      { type: 'kemik_savasci', weight: 30 },
      { type: 'lanetli_ruh', weight: 20 },
      { type: 'erlik_okcusu', weight: 15 }
    ],
    maxEnemies: 22,
    spawnInterval: 2.5,
    musicTheme: 'darkness_descent',
    ambientSounds: ['dripping', 'echoes', 'distant_screams'],
    exits: [
      {
        targetZone: 'yersub_tahti',
        // West portal: tile (8, 40) -> worldX=(8-40)*32=-1024, worldY=(8+40)*16=768
        position: { x: -1024, y: 768 },
        portalType: 'portal'
      },
      {
        targetZone: 'olum_nehri',
        // East path: tile (65, 35) -> worldX=(65-35)*32=960, worldY=(65+35)*16=1600
        position: { x: 960, y: 1600 },
        portalType: 'path'
      }
    ]
  },

  olum_nehri: {
    id: 'olum_nehri',
    name: 'Ölüm Nehri',
    description: 'Kayıp ruhların aktığı nehir.',
    tilesetId: 'death_river',
    ambientColor: '#1a0a1a',
    fogColor: '#2a1a2a',
    fogDensity: 0.5,
    levelRange: { min: 30, max: 35 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'lanetli_ruh', weight: 35 },
      { type: 'golge_avcisi', weight: 25 },
      { type: 'kemik_savasci', weight: 25 },
      { type: 'olum_habercisi', weight: 15, maxCount: 2 }
    ],
    maxEnemies: 20,
    spawnInterval: 2,
    musicTheme: 'river_of_souls',
    ambientSounds: ['water_flowing', 'souls_wailing', 'chains'],
    exits: [
      {
        targetZone: 'karanlik_tunel',
        // West path: tile (5, 30) -> worldX=(5-30)*32=-800, worldY=(5+30)*16=560
        position: { x: -800, y: 560 },
        portalType: 'path'
      },
      {
        targetZone: 'kemik_ormani',
        // East path: tile (45, 30) -> worldX=(45-30)*32=480, worldY=(45+30)*16=1200
        position: { x: 480, y: 1200 },
        portalType: 'path'
      }
    ]
  },

  kemik_ormani: {
    id: 'kemik_ormani',
    name: 'Kemik Ormanı',
    description: 'Devasa kemiklerden oluşan korkunç orman.',
    tilesetId: 'bone_forest',
    ambientColor: '#2a2a1a',
    fogColor: '#1a1a0a',
    fogDensity: 0.4,
    levelRange: { min: 32, max: 37 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'kemik_savasci', weight: 35 },
      { type: 'olum_habercisi', weight: 25, maxCount: 3 },
      { type: 'golge_avcisi', weight: 25 },
      { type: 'lanetli_ruh', weight: 15 }
    ],
    maxEnemies: 20,
    spawnInterval: 2,
    musicTheme: 'bone_whispers',
    ambientSounds: ['bones_rattling', 'wind_howl', 'growls'],
    exits: [
      {
        targetZone: 'olum_nehri',
        // West path: tile (3, 27) -> worldX=(3-27)*32=-768, worldY=(3+27)*16=480
        position: { x: -768, y: 480 },
        portalType: 'path'
      },
      {
        targetZone: 'lanetli_saray',
        // East gate: tile (52, 27) -> worldX=(52-27)*32=800, worldY=(52+27)*16=1264
        position: { x: 800, y: 1264 },
        portalType: 'gate',
        requiresLevel: 24
      }
    ]
  },

  lanetli_saray: {
    id: 'lanetli_saray',
    name: 'Lanetli Saray',
    description: 'Erlik\'in elçisinin sarayı. Act 3 final.',
    tilesetId: 'cursed_palace',
    ambientColor: '#1a0a0a',
    fogColor: '#0a0505',
    fogDensity: 0.5,
    levelRange: { min: 35, max: 40 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'olum_habercisi', weight: 35 },
      { type: 'kemik_savasci', weight: 30 },
      { type: 'golge_avcisi', weight: 20 },
      { type: 'lanetli_ruh', weight: 15 }
    ],
    bossType: 'erlik_elcisi',
    maxEnemies: 22,
    spawnInterval: 2,
    musicTheme: 'palace_doom',
    ambientSounds: ['dark_chants', 'screams', 'thunder'],
    exits: [
      {
        targetZone: 'kemik_ormani',
        // South entry: tile (25, 52) -> worldX=(25-52)*32=-864, worldY=(25+52)*16=1232
        position: { x: -864, y: 1232 },
        portalType: 'path'
      },
      {
        targetZone: 'erlik_kapisi',
        // North throne: tile (25, 5) -> worldX=(25-5)*32=640, worldY=(25+5)*16=480
        position: { x: 640, y: 480 },
        portalType: 'portal',
        requiresLevel: 28
      }
    ]
  },

  // ==========================================
  // ACT 4: ERLIK DIYARI (Placeholder)
  // ==========================================

  erlik_kapisi: {
    id: 'erlik_kapisi',
    name: 'Erlik Kapısı',
    description: 'Erlik\'in diyarına açılan kapı. Act 4 başlangıcı.',
    tilesetId: 'hell_gate',
    ambientColor: '#2a0a0a',
    fogColor: '#1a0505',
    fogDensity: 0.6,
    levelRange: { min: 28, max: 35 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'erlik_sovalyesi', weight: 35 },
      { type: 'karanlik_seraf', weight: 25 },
      { type: 'olum_habercisi', weight: 25 },
      { type: 'kemik_savasci', weight: 15 }
    ],
    maxEnemies: 22,
    spawnInterval: 1.8,
    musicTheme: 'hell_march',
    ambientSounds: ['fire_roaring', 'screams', 'drums'],
    exits: [
      {
        targetZone: 'lanetli_saray',
        // South entry: tile (22, 47) -> worldX=(22-47)*32=-800, worldY=(22+47)*16=1104
        position: { x: -800, y: 1104 },
        portalType: 'portal'
      },
      {
        targetZone: 'cehennem_bahcesi',
        // North exit: tile (22, 4) -> worldX=(22-4)*32=576, worldY=(22+4)*16=416
        position: { x: 576, y: 416 },
        portalType: 'path',
        requiresLevel: 32
      }
    ]
  },

  cehennem_bahcesi: {
    id: 'cehennem_bahcesi',
    name: 'Cehennem Bahçesi',
    description: 'Erlik\'in korkunç bahçesi. Karanlık melekler burada uçar.',
    tilesetId: 'hell_garden',
    ambientColor: '#3a0a0a',
    fogColor: '#2a0505',
    fogDensity: 0.5,
    levelRange: { min: 32, max: 40 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'karanlik_seraf', weight: 35 },
      { type: 'erlik_sovalyesi', weight: 30 },
      { type: 'olum_habercisi', weight: 20, maxCount: 2 },
      { type: 'golge_avcisi', weight: 15 }
    ],
    maxEnemies: 22,
    spawnInterval: 1.8,
    musicTheme: 'garden_of_doom',
    ambientSounds: ['fire', 'screams', 'dark_whispers'],
    exits: [
      {
        targetZone: 'erlik_kapisi',
        // West path: tile (3, 25) -> walkable (rocky_ground base)
        position: { x: -704, y: 448 },
        portalType: 'path'
      },
      {
        targetZone: 'taht_odasi',
        // East gate: tile (50, 25) -> worldX=(50-25)*32=800, worldY=(50+25)*16=1200
        position: { x: 800, y: 1200 },
        portalType: 'gate',
        requiresLevel: 35
      }
    ]
  },

  taht_odasi: {
    id: 'taht_odasi',
    name: 'Taht Odası',
    description: 'Erlik Han\'ın taht odası. Yeraltı Hükümdarı ile son savaş.',
    tilesetId: 'throne_room',
    ambientColor: '#1a0505',
    fogColor: '#0a0202',
    fogDensity: 0.4,
    levelRange: { min: 35, max: 50 },
    isSafeZone: false,
    enemySpawns: [
      { type: 'erlik_sovalyesi', weight: 40 },
      { type: 'karanlik_seraf', weight: 35 },
      { type: 'olum_habercisi', weight: 25, maxCount: 2 }
    ],
    bossType: 'erlik_han',
    maxEnemies: 25,
    spawnInterval: 2,
    musicTheme: 'final_confrontation',
    ambientSounds: ['dark_power', 'thunder', 'chants'],
    exits: [
      {
        targetZone: 'cehennem_bahcesi',
        // South entry: tile (30, 62) -> worldX=(30-62)*32=-1024, worldY=(30+62)*16=1472
        position: { x: -1024, y: 1472 },
        portalType: 'gate'
      }
    ]
  }
};

// Zone manager class
export class ZoneManager {
  private currentZone: ZoneConfig;
  private zoneHistory: ZoneType[] = [];

  constructor(startingZone: ZoneType = 'karanlik_orman') {
    this.currentZone = ZONES[startingZone];
    this.zoneHistory.push(startingZone);
  }

  getCurrentZone(): ZoneConfig {
    return this.currentZone;
  }

  getZone(zoneId: ZoneType): ZoneConfig {
    return ZONES[zoneId];
  }

  canEnterZone(zoneId: ZoneType, playerLevel: number): { canEnter: boolean; reason?: string } {
    // Find exit from current zone to target
    const exit = this.currentZone.exits.find(e => e.targetZone === zoneId);

    if (!exit) {
      return { canEnter: false, reason: t('zone.noTransition') };
    }

    // Check level requirement
    if (exit.requiresLevel && playerLevel < exit.requiresLevel) {
      return { canEnter: false, reason: t('zone.levelRequired', { level: exit.requiresLevel }) };
    }

    return { canEnter: true };
  }

  changeZone(zoneId: ZoneType): ZoneConfig {
    this.currentZone = ZONES[zoneId];
    this.zoneHistory.push(zoneId);
    return this.currentZone;
  }

  getEnemySpawnWeights(playerLevel: number): EnemySpawnConfig[] {
    return this.currentZone.enemySpawns.filter(spawn => {
      if (spawn.minLevel && playerLevel < spawn.minLevel) {
        return false;
      }
      return true;
    });
  }

  selectEnemyType(playerLevel: number): EnemySpawnType | null {
    const validSpawns = this.getEnemySpawnWeights(playerLevel);

    if (validSpawns.length === 0) return null;

    const totalWeight = validSpawns.reduce((sum, s) => sum + s.weight, 0);
    let roll = Math.random() * totalWeight;

    for (const spawn of validSpawns) {
      roll -= spawn.weight;
      if (roll <= 0) {
        return spawn.type;
      }
    }

    return validSpawns[0].type;
  }

  getExits(): ZoneExit[] {
    return this.currentZone.exits;
  }

  getExitAt(x: number, y: number, threshold: number = 50): ZoneExit | null {
    for (const exit of this.currentZone.exits) {
      const dx = x - exit.position.x;
      const dy = y - exit.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < threshold) {
        return exit;
      }
    }
    return null;
  }
}
